// CTSK-255 — Registry-append validation primitive (Knowledge-Data
// Platform, CTSK-254 EPIC).
//
// One `.strict()` Zod schema per registry-file schema key (the enum on
// RegistryFileEntry in tool.ts), plus a total dispatcher the append
// endpoint calls BEFORE mutating the YAML file. Write-time validation
// replaces the CTSK-251 stance ("entry shape is schema-owned, the
// server does not re-validate") after two incidents where off-shape
// entries landed in a registry file and broke downstream readers at
// read time (viz, DSR table renderer, tootasko-clipper).
//
// Design rules:
//   - Source of truth is the ACTUAL YAML files in registry/ (shapes
//     sampled 2026-07-31), not any prior doc. Where the file's own
//     schema-doc header block declares required-ness, we follow it.
//   - Permissive-but-strict: fields observed in only some entries are
//     `.optional()`; enum-ish string fields (type/status/track/...)
//     stay plain strings so operator vocabulary can grow without an
//     API deploy — but UNKNOWN KEYS ARE ALWAYS REJECTED (`.strict()`).
//     That's the whole point: wrong field names must fail loudly, not
//     silently drift the file (see feedback_zod_field_name_silent_drop).
//   - Dates: events use plain `z.string()` — BCE dates are negative-
//     year strings like '-2686-01-01' which `z.string().date()` /
//     `.datetime()` would reject. Markers additionally use the literal
//     'dynamic:today'. Only bookmarks.captured_at is a real ISO 8601
//     timestamp (producers emit it programmatically).
import { z } from 'zod';
// ── data-sources-v1 (DSR data_sources.yaml) ─────────────────────
//
// CTSK-303 — the entry shape is now kind-discriminated:
//
//   kind: 'external-url'       (default — every pre-CTSK-303 entry)
//   kind: 'internal-registry'  (NEW — a CT tool's own registry file,
//                               self-registered at manifest install /
//                               backfill so the DSR page answers "what
//                               data flows into CT via which endpoint")
//
// Backward compat rule: `kind` absent → 'external-url', and the
// external shape below is EXACTLY the pre-CTSK-303 schema (no field
// added/removed beyond the optional discriminator) so every existing
// entry keeps validating byte-for-byte.
/** The two data-source kinds (CTSK-303). */
export const DATA_SOURCE_KINDS = ['external-url', 'internal-registry'];
// External-url variant — the original entry shape.
//
// Observed entry shape (registry container data_sources.yaml):
//   required: id, name, type, status
//   optional: description, url, owner{name,email}, update_frequency,
//             subscription, license, cost_annual_usd (number|null),
//             tools_used[], tags[], last_reviewed, notes
// type/status/update_frequency/subscription have documented allowed
// values in the file header, but real entries already vary in case and
// vintage — kept as plain strings (tightening is a follow-up FR).
export const ExternalUrlDataSourceEntrySchema = z.object({
    /** CTSK-303 discriminator. Pre-existing entries omit it; the
     *  dispatcher (DataSourceEntrySchema below) injects the default
     *  before parsing so this literal always matches. */
    kind: z.literal('external-url'),
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.string().min(1),
    status: z.string().min(1),
    description: z.string().optional(),
    url: z.string().optional(), // postgres:// & sftp:// URLs in real data — no .url()
    owner: z.object({
        name: z.string().optional(),
        email: z.string().optional(),
    }).strict().optional(),
    update_frequency: z.string().optional(),
    subscription: z.string().optional(),
    license: z.string().optional(),
    cost_annual_usd: z.number().nullable().optional(),
    tools_used: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    last_reviewed: z.string().optional(),
    notes: z.string().optional(),
}).strict();
// Internal-registry variant (CTSK-303). One entry per (target_tool,
// target_file) — that pair is the upsert natural key the api-side
// helper (apps/api/src/lib/dsr-upsert.ts) is idempotent on.
export const InternalRegistryDataSourceEntrySchema = z.object({
    kind: z.literal('internal-registry'),
    id: z.string().min(1),
    name: z.string().min(1),
    /** Slug of the CT tool whose registry file this entry describes. */
    target_tool: z.string().min(1),
    /** The registry file path within that tool's bound container. */
    target_file: z.string().min(1),
    /** Endpoint template producers push through (e.g.
     *  'POST /api/v1/tools/<id>/registry/append'). Plain string — it is
     *  a template with a method prefix, not a fetchable URL. */
    endpoint: z.string().min(1),
    direction: z.enum(['inbound', 'bidirectional']),
    /** Producer identities known to push into this file. Grows over
     *  time; empty for a freshly-registered file. */
    known_producers: z.array(z.string()),
    /** Who created this entry: 'manifest-install:<toolId>',
     *  'bundle:<uid>', 'backfill:<script>', or an operator string. */
    installed_by: z.string().min(1),
    installed_at: z.string().datetime({ offset: true }),
    description: z.string().optional(),
    status: z.string().optional(),
    tags: z.array(z.string()).optional(),
    notes: z.string().optional(),
    /** CTSK-366 — canonical pointer to the schema this entry's target
     *  file conforms to. One-of documentId (a CT document holding the
     *  schema) or url (external JSON Schema / YAML / etc.); label is
     *  display-only and falls back to "Schema" in the renderer. Neither
     *  sub-field is required — an entry can carry an empty schemaRef
     *  object, though the DSR table renders no chip in that case (see
     *  DataSourcesTable). Not validated against the target file's actual
     *  entries — that's CTSK-255 territory, out of scope here. */
    schemaRef: z.object({
        documentId: z.string().optional(),
        url: z.string().url().optional(),
        label: z.string().optional(),
    }).strict().optional(),
}).strict();
/** Inject the default kind BEFORE the discriminated union parses, so
 *  (a) pre-CTSK-303 entries without `kind` hit the external-url branch
 *  unchanged, and (b) the discriminator stays a plain ZodLiteral
 *  (z.discriminatedUnion rejects default()-wrapped discriminators). */
function withDefaultKind(val) {
    if (val !== null && typeof val === 'object' && !Array.isArray(val) && !('kind' in val)) {
        return { ...val, kind: 'external-url' };
    }
    return val;
}
export const DataSourceEntrySchema = z.preprocess(withDefaultKind, z.discriminatedUnion('kind', [
    ExternalUrlDataSourceEntrySchema,
    InternalRegistryDataSourceEntrySchema,
]));
// ── bookmarks-v1 (DSR bookmarks.yaml) ───────────────────────────
//
// Per the file's own schema-doc header (registry/bookmarks.yaml):
//   required: id, url, title, captured_at (ISO 8601), source
//   optional: note, tags
// NB the header names `note` (singular) — NOT `notes`. This is the
// tootasko-clipper "Save as DSR Bookmark" shape; the highest-traffic
// append producer, so required-ness here mirrors what it emits.
export const BookmarkEntrySchema = z.object({
    id: z.string().min(1),
    url: z.string().url(),
    title: z.string().min(1),
    captured_at: z.string().datetime({ offset: true }),
    source: z.string().min(1),
    note: z.string().optional(),
    tags: z.array(z.string()).optional(),
    // CTSK-364 — freeform per-tool namespace for fields captured back
    // from external tools via rowActions fetch-and-map responseMap
    // (first tenant: external_refs.hyper_scan.{uuid,card_url,audit_url,
    // status_url}). Keyed by tool slug so any future integration can
    // piggyback without a schema bump; values are freeform per field.
    external_refs: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
}).strict();
// ── events-v1 (Timeline Lane events.yaml — deep-time) ───────────
//
// Documented viewer schema (audit-timeline-events.yaml header block):
//   required: name, date, track, cat
//   recommended: id, endDate, location, desc, sourceLabel, sourceUrl, tags
// `date`/`endDate` are plain strings: BCE entries use negative years
// ('-2686-01-01') which no Zod date/datetime format accepts.
// `tags` accepts numbers too — real entries carry unquoted numeric
// year tags (leonid-meteor-storm-1833 has tag 1833) that YAML parses
// as numbers. Loosened per the CTSK-255 escalation rule (existing
// valid entries MUST pass); tightening is noted in FEATURE_REQUESTS.
export const EventEntrySchema = z.object({
    name: z.string().min(1),
    date: z.string().min(1),
    track: z.string().min(1),
    cat: z.string().min(1),
    id: z.string().min(1).optional(),
    endDate: z.string().min(1).optional(),
    location: z.string().optional(),
    desc: z.string().optional(),
    sourceLabel: z.string().optional(),
    sourceUrl: z.string().url().optional(),
    tags: z.array(z.union([z.string(), z.number()])).optional(),
}).strict();
// ── audit-timeline-v1 (workspace audit log) ─────────────────────
//
// Same viewer schema as events-v1 (the audit file's header block is
// where the required-set is documented). Kept as a SEPARATE schema —
// not an alias — so the two can drift independently (audit entries
// will likely grow machine-stamped fields the deep-time file never
// has). sourceUrl here carries file:/// URLs, which new URL() accepts.
export const AuditTimelineEntrySchema = z.object({
    name: z.string().min(1),
    date: z.string().min(1),
    track: z.string().min(1),
    cat: z.string().min(1),
    id: z.string().min(1).optional(),
    endDate: z.string().min(1).optional(),
    location: z.string().optional(),
    desc: z.string().optional(),
    sourceLabel: z.string().optional(),
    sourceUrl: z.string().url().optional(),
    tags: z.array(z.string()).optional(),
}).strict();
// ── timeline-marker-v1 (markers.yaml + future-events.yaml) ──────
//
// Marker entries are events-with-a-render-block. Observed shape:
//   required: name, date, track, cat   (same core as the trio)
//   optional: id, type ('timeline-marker'), endDate (windows),
//             location, desc, sourceLabel, sourceUrl, tags,
//             marker{enabled,label,color,width,opacity,dasharray,
//                    scope,display,windowOpacity}
// `date` may be the literal 'dynamic:today' (browser-resolved) — plain
// string, no format check. The nested marker block is ALSO .strict():
// a typo'd render key (colour/dash_array) should fail the append, not
// silently render with defaults.
export const TimelineMarkerEntrySchema = z.object({
    name: z.string().min(1),
    date: z.string().min(1),
    track: z.string().min(1),
    cat: z.string().min(1),
    id: z.string().min(1).optional(),
    type: z.string().optional(),
    endDate: z.string().min(1).optional(),
    location: z.string().optional(),
    desc: z.string().optional(),
    sourceLabel: z.string().optional(),
    sourceUrl: z.string().url().optional(),
    tags: z.array(z.string()).optional(),
    marker: z.object({
        enabled: z.boolean().optional(),
        label: z.string().optional(),
        color: z.string().optional(),
        width: z.number().optional(),
        opacity: z.number().optional(),
        dasharray: z.string().optional(),
        scope: z.string().optional(),
        display: z.string().optional(),
        windowOpacity: z.number().optional(),
    }).strict().optional(),
}).strict();
/**
 * CTSK-255 — validate one candidate append entry against its file's
 * registered schema. Total over ANY string schema key (unknown keys
 * return `{ ok: false }` with a single 'unknown_schema' issue rather
 * than throwing) so the append endpoint and future callers (nightly
 * drift scan — see FEATURE_REQUESTS) share one failure mode.
 *
 * On success, `entry` is the Zod-parsed value: same fields, with the
 * static guarantee that no unknown keys survived.
 */
export function validateRegistryEntry(schema, entry) {
    const validator = VALIDATORS[schema];
    if (!validator) {
        return {
            ok: false,
            errors: [{
                    code: 'unknown_schema',
                    path: [],
                    message: `no validator for schema '${schema}'`,
                }],
        };
    }
    const result = validator.safeParse(entry);
    if (result.success)
        return { ok: true, entry: result.data };
    return {
        ok: false,
        errors: result.error.issues.map((iss) => ({
            code: iss.code,
            path: iss.path,
            message: iss.message,
        })),
    };
}
// ── CTSK-304 — entry id auto-generation ─────────────────────────
//
// The append primitive auto-generates a name-derived slug id when the
// payload omits one, so no entry ever lands id-less (the events family
// keeps `id` optional at validation — every OTHER schema requires it,
// so those reject id-less payloads before generation is reached). The
// generator lives here, next to the validators, so server and future
// callers (clean --migrate, importers) share one convention.
//
// Convention mirrors the hand-maintained files: lowercase, hyphen-
// separated, ASCII ('Battle of Adwa' → 'battle-of-adwa'). Caller-
// provided ids are NEVER overwritten — generation only fills a hole.
/** Slugify a display name into the registry id style. Returns '' when
 *  nothing slug-able survives (e.g. all-symbol input) — callers treat
 *  that as "cannot generate". */
export function slugifyRegistryName(name, max = 64) {
    return name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, max)
        .replace(/-+$/, '');
}
/**
 * Generate an id for an entry that arrived without one. Derives a slug
 * from `name` (falling back to `title` for bookmark-shaped entries) and
 * uniquifies against `existingIds` with a `-2`, `-3`, ... suffix so a
 * generated id can never collide with an entry already in the file.
 *
 * Returns null when no name-ish field is present or nothing slug-able
 * survives — the caller keeps its legacy behaviour (append id-less)
 * rather than inventing an opaque id the operator never chose.
 *
 * `schema` is accepted for forward-compat (per-schema id prefixes like
 * `bkm_...` are a plausible follow-up) but does not change the derived
 * slug today.
 */
export function generateRegistryEntryId(schema, entry, existingIds) {
    void schema;
    const nameSource = (typeof entry.name === 'string' && entry.name.length > 0 ? entry.name : null)
        ?? (typeof entry.title === 'string' && entry.title.length > 0 ? entry.title : null);
    if (nameSource === null)
        return null;
    const base = slugifyRegistryName(nameSource);
    if (base.length === 0)
        return null;
    if (!existingIds.has(base))
        return base;
    for (let n = 2; n < 10_000; n += 1) {
        const candidate = `${base}-${n}`;
        if (!existingIds.has(candidate))
            return candidate;
    }
    return null; // 10k same-name entries: give up rather than loop forever
}
// Dispatcher table — one entry per RegistryFileSchema value. The
// Record type makes adding a schema key to tool.ts a compile error
// here until a validator lands, so the enum and the validators can
// never drift apart.
const VALIDATORS = {
    'data-sources-v1': DataSourceEntrySchema,
    'bookmarks-v1': BookmarkEntrySchema,
    'events-v1': EventEntrySchema,
    'audit-timeline-v1': AuditTimelineEntrySchema,
    'timeline-marker-v1': TimelineMarkerEntrySchema,
};
//# sourceMappingURL=registry-entries.js.map